import { print } from "graphql";
import type { DocumentNode } from "graphql";
import { env } from "@/shared/config";
import { getAuthToken } from "@/shared/utils/auth";
import { getAcceptLanguageHeader } from "@/shared/utils/acceptLanguage";
import { useImpersonationStore } from "@/features/user/model/impersonationStore";
import { graphqlMockResolve } from "./graphqlMockResolver";

const GRAPHQL_URL = `${env.api.url.replace("/api/v1", "")}/graphql`;

export async function graphqlFetch<T>(
  document: DocumentNode | string,
  variables?: Record<string, unknown>
): Promise<T> {
  if (env.api.useMockData) {
    return graphqlMockResolve<T>(document, variables);
  }

  const query = typeof document === "string" ? document : print(document);
  const token = getAuthToken();
  const impersonation = useImpersonationStore.getState();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": getAcceptLanguageHeader(),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (impersonation.isImpersonating() && impersonation.impersonatedContext?.id) {
    headers["x-act-as-user-id"] = impersonation.impersonatedContext.id;
  }

  const response = await fetch(GRAPHQL_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
  }

  const json = (await response.json()) as { data: T; errors?: Array<{ message: string }> };
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join(", "));
  }
  return json.data;
}
